/**
 * Location functions are used for reaching the endpoints for location.
 * @module Location
 * @since 0.0.8
 */
import GlobalFunctions from '../global'

module.exports = {
	/**
	 * Call to unoapp API that returns an array of locations for the specified page and search criteria.
	 * @function
	 * @param {integer} businessId - The id of the business.
	 * @param {integer} pageNumber - The current page that we are retrieving results for.
	 * @param {integer} recordsPerPage - The number of records to return per page.
	 * @param {object} searchCriteria - Object with all criteria to be searched by.
	 * @param {object} sortCriteria - The field and order to be sorted by.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.7
	 */
	getLocations (businessId, pageNumber, recordsPerPage, searchCriteria, sortCriteria) {
		let httpParams = {business_id: businessId, page_number: pageNumber, records_per_page: recordsPerPage}
		if (sortCriteria) {
			for (let sortKey in sortCriteria) {
				httpParams[sortKey] = sortCriteria[sortKey]
			}
		}

		if (searchCriteria) {
			for (let searchKey in searchCriteria) {
				httpParams[searchKey] = searchCriteria[searchKey]
			}
		}
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/location/search',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a new location.
	 * @function
	 * @param {object} businessId - The id of the business where the location is being created.
	 * @param {object} location - The location to be created.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.8
	 */
	createLocation (businessId, location) {
		let httpParams = {
			business_id: businessId,
			region_id: location.region.id,
			name: location.location_name,
			unoapp_defined_name: location.unoapp_defined_name
		}

		if (location.selected_location) {
			httpParams.latitude = location.selected_location.coordinates.lat
			httpParams.longitude = location.selected_location.coordinates.lng

			let streetAddress = {}
			location.selected_location.address_components.forEach(component => {
				component.types.forEach(type => {
					switch (type) {
					case 'street_address':
						httpParams.address_line_1 = component.long_name
						break

					case 'route':
					case 'premise':
						streetAddress.route = component.long_name
						break

					case 'street_number':
					case 'subpremise':
						streetAddress.street_number = component.long_name
						break

					case 'locality':
						httpParams.city = component.long_name
						break

					case 'country':
						httpParams.country = component.long_name
						break

					case 'postal_code':
						httpParams.postal_code = component.long_name
						break

					case 'administrative_area_level_1':
						httpParams.province = component.long_name
						break

					default:
						// No default
					}
				})
			})

			if (!httpParams.street_address) {
				httpParams.street_address = `${streetAddress.street_number} ${streetAddress.route}`
			}
		}

		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/location',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that retrieves an existing location.
	 * @function
	 * @param {object} locationId - The id of the location to be retrieved.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	getLocation (locationId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/location/' + locationId,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that modifies an existing role.
	 * @function
	 * @param {object} location - The location to be modified.
	 * @param {boolean} bypassConflict - Whether or not we should bypass any conflicts.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	modifyLocation (location, bypassConflict) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PATCH',
				dataType: 'json',
				url: '/location/' + location.location_id,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that retrieves location detials.
	 * @function
	 * @param {object} locationId - The id of the location to get details for.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	getLocationDetails (locationId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/location/' + locationId,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	}
}



// WEBPACK FOOTER //
// ./src/controllers/Location.js